// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Button, InputBase, IconButton, Tooltip, FormHelperText } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { fieldErrorMessage } from 'Utils/apiResponse';
import stylesDef from './styles';

interface InlineNameEditorProps {
  name: string;
  ariaLabel: string;
  fallbackError: string;
  onSave: (name: string) => Promise<IApiResponse<unknown>>;
  canEdit?: boolean;
}

/**
 * A name shown as text that turns into an input on double-click or via the edit icon. Enter or the
 * check icon saves; Escape or the close icon discards (the icon matters: a browser in fullscreen
 * consumes Escape to leave fullscreen and never delivers it to the page). A save the server rejects
 * (e.g. the name is already taken) keeps the input open and shows the reason under it. The saved
 * name stays displayed until the `name` prop catches up, because not every store slice updates the
 * renamed record.
 */
export const InlineNameEditor = ({ name, ariaLabel, fallbackError, onSave, canEdit = true }: InlineNameEditorProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  // Name saved here that the `name` prop has not caught up with yet (null: the prop is current)
  const [savedName, setSavedName] = useState<string | null>(null);
  // Text being edited (null: not editing)
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState('');

  const currentName = savedName ?? name;
  const isEditing = draft !== null;

  // A new name from the store (or a different record) replaces whatever was typed or saved here
  useEffect(() => {
    setSavedName(null);
    setDraft(null);
    setError('');
  }, [name]);

  const startEditing = () => {
    if (!canEdit) return;
    setDraft(currentName);
    setError('');
  };

  const cancelEditing = () => {
    setDraft(null);
    setError('');
  };

  const save = async () => {
    const newName = (draft ?? '').trim();
    if (!newName || newName === currentName) {
      cancelEditing();
      return;
    }
    try {
      const result = await onSave(newName);
      if (!result.success) {
        setError(fieldErrorMessage(result, 'name', fallbackError));
        return;
      }
      setSavedName(newName);
      cancelEditing();
    } catch (e) {
      setError(fallbackError);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      save();
    } else if (e.key === 'Escape') {
      cancelEditing();
    } else {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box sx={styles.container}>
      {isEditing ? (
        <Box sx={styles.inputWrapper}>
          <InputBase
            sx={styles.inputField}
            autoFocus
            error={!!error}
            value={draft ?? ''}
            inputProps={{ 'aria-label': ariaLabel }}
            onChange={(e) => {
              setDraft(e.target.value);
              setError('');
            }}
            onKeyDown={handleKeyDown}
          />
          {error && <FormHelperText error sx={styles.errorText}>{error}</FormHelperText>}
        </Box>
      ) : (
        <Tooltip title={currentName} arrow placement="top">
          <Button sx={styles.titleButton} variant="text" disableTouchRipple onDoubleClick={startEditing}>
            <Box component="span" sx={styles.titleText}>{currentName}</Box>
          </Button>
        </Tooltip>
      )}
      {canEdit && (isEditing ? (
        <Box sx={styles.editControls}>
          <IconButton aria-label="check" onClick={save}>
            <CheckIcon />
          </IconButton>
          <IconButton aria-label="cancel" onClick={cancelEditing}>
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <IconButton aria-label="edit" onClick={startEditing}>
          <EditIcon />
        </IconButton>
      ))}
    </Box>
  );
};

InlineNameEditor.defaultProps = {
  canEdit: true,
};

export default InlineNameEditor;
